import { useState, useEffect } from 'react'
import { RoleValidationService } from '../lib/services/roleValidationService'

export interface GroupPermissions {
  canManage: boolean
  canManageCurriculum: boolean
  canManageEvents: boolean
  canDeleteContent: boolean
  canViewAnalytics: boolean
  canManageSettings: boolean
  canAssignTasks: boolean
  canViewGroupData: boolean
  canViewCurriculum: boolean
  canViewSchedule: boolean
  canEditOwnTasks: boolean
  isLoading: boolean
  error?: string
}

export const useGroupPermissions = (userId: string, groupId: string): GroupPermissions => {
  const [permissions, setPermissions] = useState<GroupPermissions>({
    canManage: false,
    canManageCurriculum: false,
    canManageEvents: false,
    canDeleteContent: false,
    canViewAnalytics: false,
    canManageSettings: false,
    canAssignTasks: false,
    canViewGroupData: false,
    canViewCurriculum: false,
    canViewSchedule: false,
    canEditOwnTasks: false,
    isLoading: true
  })

  useEffect(() => {
    const loadPermissions = async () => {
      if (!userId || !groupId) {
        setPermissions(prev => ({ ...prev, isLoading: false }))
        return
      }

      try {
        const [
          management,
          curriculum,
          events,
          contentDeletion,
          analytics,
          settings,
          taskAssignment,
          groupData
        ] = await Promise.allSettled([
          RoleValidationService.validateGroupManagement(userId, groupId),
          RoleValidationService.validateCurriculumManagement(userId, groupId),
          RoleValidationService.validateEventManagement(userId, groupId),
          RoleValidationService.validateContentDeletion(userId, groupId),
          RoleValidationService.validateAnalyticsAccess(userId, groupId),
          RoleValidationService.validateGroupSettingsManagement(userId, groupId),
          RoleValidationService.validateTaskAssignment(userId, groupId),
          RoleValidationService.validateGroupDataAccess(userId, groupId)
        ])

        // Extract results from Promise.allSettled
        const managementResult = management.status === 'fulfilled' ? management.value : { isValid: false }
        const curriculumResult = curriculum.status === 'fulfilled' ? curriculum.value : { isValid: false }
        const eventsResult = events.status === 'fulfilled' ? events.value : { isValid: false }
        const contentDeletionResult = contentDeletion.status === 'fulfilled' ? contentDeletion.value : { isValid: false }
        const analyticsResult = analytics.status === 'fulfilled' ? analytics.value : { isValid: false }
        const settingsResult = settings.status === 'fulfilled' ? settings.value : { isValid: false }
        const taskAssignmentResult = taskAssignment.status === 'fulfilled' ? taskAssignment.value : { isValid: false }
        const groupDataResult = groupData.status === 'fulfilled' ? groupData.value : { isValid: false }

        setPermissions({
          canManage: managementResult.isValid,
          canManageCurriculum: curriculumResult.isValid,
          canManageEvents: eventsResult.isValid,
          canDeleteContent: contentDeletionResult.isValid,
          canViewAnalytics: analyticsResult.isValid,
          canManageSettings: settingsResult.isValid,
          canAssignTasks: taskAssignmentResult.isValid,
          canViewGroupData: groupDataResult.isValid,
          canViewCurriculum: curriculumResult.isValid || groupDataResult.isValid, // Members can view if they have curriculum or group data access
          canViewSchedule: curriculumResult.isValid || groupDataResult.isValid, // Members can view schedule
          canEditOwnTasks: taskAssignmentResult.isValid, // Members can edit their own tasks
          isLoading: false
        })
      } catch (error) {
        setPermissions({
          canManage: false,
          canManageCurriculum: false,
          canManageEvents: false,
          canDeleteContent: false,
          canViewAnalytics: false,
          canManageSettings: false,
          canAssignTasks: false,
          canViewGroupData: false,
          canViewCurriculum: false,
          canViewSchedule: false,
          canEditOwnTasks: false,
          isLoading: false,
          error: 'Failed to load permissions'
        })
      }
    }

    loadPermissions()
  }, [userId, groupId])

  return permissions
}

export const useGlobalPermissions = (userId: string) => {
  const [permissions, setPermissions] = useState({
    isSuperAdmin: false,
    isAdmin: false,
    isLoading: true,
    error: undefined as string | undefined
  })

  useEffect(() => {
    const loadPermissions = async () => {
      if (!userId) {
        setPermissions(prev => ({ ...prev, isLoading: false }))
        return
      }

      try {
        const [superAdmin, admin] = await Promise.all([
          RoleValidationService.validateSuperAdmin(userId),
          RoleValidationService.validateAdmin(userId)
        ])

        setPermissions({
          isSuperAdmin: superAdmin.isValid,
          isAdmin: admin.isValid,
          isLoading: false,
          error: undefined
        })
      } catch (error) {
        setPermissions({
          isSuperAdmin: false,
          isAdmin: false,
          isLoading: false,
          error: 'Failed to load global permissions'
        })
      }
    }

    loadPermissions()
  }, [userId])

  return permissions
} 